import { useEffect, useRef } from "react";
import type { ShoulderSurfConfig } from "../scenario/types";
import { ChoiceCard, ChoiceList } from "../ui/ChoiceCard";
import { audio } from "../lib/audio";
import { tx } from "../lib/tx";

export function ShoulderSurf({
  config,
  inspected,
  onChoose,
}: {
  config: ShoulderSurfConfig;
  /** true once the learner has used Inspect on this beat. */
  inspected: boolean;
  onChoose: (opt: ShoulderSurfConfig["options"][number]) => void;
}) {
  const spoke = useRef(false);
  useEffect(() => {
    // Prefetch every line this device can speak so the voice pre-render captures branch lines.
    if (config.lenaSafe) void audio.prefetchSpeech(config.lenaSafe, { voice: "nova", lang: config.language ?? "en-US" });
    if (config.comicFail) void audio.prefetchSpeech(config.comicFail, { voice: "echo", lang: config.language ?? "en-US" });
    if (spoke.current) return;
    spoke.current = true;
    void audio.speak(config.body ?? "Max wants to open the pricing document.", {
      voice: config.voiceId ?? "onyx",
      lang: config.language ?? "en-US",
    });
    return () => audio.cancelSpeech();
  }, [config]);

  const require = config.requireInspect !== false;

  return (
    <div className="rw-panel shouldersurf">
      <div className="rw-kicker">{tx("Train carriage")}</div>
      <h2>{config.headline ?? tx("Shoulder surfing check")}</h2>
      {config.body && <p className="rw-body">{config.body}</p>}
      {require && !inspected && (
        <p className="rw-hint">
          {config.inspectHint ??
            tx("Use Look around to check whether the screen is visible from other seats.")}
        </p>
      )}
      <ChoiceList prompt={tx("What should Max do first?")}>
        {config.options.map((o) => {
          const locked = !!(require && o.needsInspect && !inspected);
          return (
            <ChoiceCard
              key={o.id}
              category={o.category}
              showRiskLabel={false}
              reply={o.label}
              disabled={locked}
              meaning={locked ? tx("Look around first") : undefined}
              onClick={() => {
                if (locked) return;
                if (o.safe && config.lenaSafe)
                  void audio.speak(config.lenaSafe, {
                    voice: "nova",
                    lang: config.language ?? "en-US",
                  });
                else if (config.comicFail)
                  void audio.speak(config.comicFail, {
                    voice: "echo",
                    lang: config.language ?? "en-US",
                  });
                onChoose(o);
              }}
            />
          );
        })}
      </ChoiceList>
      {!require || inspected ? null : (
        <p className="rw-comic" aria-live="polite">
          {tx("Look around — the passenger across the aisle can see the screen.")}
        </p>
      )}
    </div>
  );
}
